// CONTRACT:I4-POLICY-DECISION.1.0
/** seamPolicy — the PURE policy logic for the C3 capability/AI seams (the actor
 *  access model, docs/adr/0008-actor-access-model.md). Two exports back the two
 *  enforcement points the COP exposes to an extension/AI:
 *    - decideForResource()  →  HostContext.policy.decide (the command/activation gate)
 *    - isVisibleToSeam()    →  the PDP-interposed HostContext.data.subscribe filter
 *  Both delegate to the SAME vendored decideVisibility the render path + the lockstep
 *  oracle use, so the three enforcement points (render / data seam / command decide)
 *  can never diverge. Pure: zero DOM / network / crypto — proven by
 *  apps/cop/scripts/seam-check.ts WITHOUT the live stack.
 *
 *  TDFLite-safe (the acceptance gate): reads only the bundle + claims and returns a
 *  permit/deny bit + reason. It NEVER touches the JWT, KAS subject, @opentdf/sdk
 *  rewrap, or the token request, and it is strictly SUBTRACTIVE (can deny a
 *  contribution; can never release what the KAS would deny). */

import {
  decideVisibility,
  type PolicyBundle,
  type SubjectClaims,
} from "../../../../packages/abac-pdp/src/index.ts";
import type { SecurityLabels, CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";
import type { PolicyDecision } from "../../../../packages/contracts-ts/src/index.ts";
// SINGLE SOURCE OF TRUTH: reuse the PDP chokepoint's three-case classifier directly
// (same package as pdp.ts) — do NOT re-implement the taxonomy here (avoids FINDINGS
// F-LATTICE-1 drift). A BROKEN-PRESENT marking at the seam must fail-closed exactly
// as it does at the render/recompute chokepoint.
import { classifyMarking } from "./pdp.ts";

/** The CONSTANT, non-revealing reason returned for EVERY deny at the capability/AI seam
 *  (Glomar): it must be identical for all denied resources so it leaks nothing about the
 *  withheld object's classification/compartment. Exported so the invariant checks can assert it. */
export const DENY_REASON = "not authorized";

/** Decide a capability/command request against the live entitlements.
 *
 *  LEGACY-PERMISSIVE (load-bearing back-compat, ADR-0008 A1): a request whose
 *  resource carries NO classification label is UNGATED → permit. The closed
 *  deny-default applies only to label-carrying (opt-in) resources, so every existing
 *  capability/command keeps activating and lockstep stays 40/40. A label-carrying
 *  resource is decided by the SAME decideVisibility the rendered picture uses, so the
 *  command gate and the visibility filter agree by construction. */
export function decideForResource(
  resource: SecurityLabels | undefined,
  bundle: PolicyBundle,
  claims: SubjectClaims,
): PolicyDecision {
  // THREE-CASE TAXONOMY (docs/AOA-CLASSID-RECOMPUTE.md). The old guard
  // `if (!resource || !resource.classification) permit` CONFLATED genuinely-unmarked
  // (Case 1) with a marking-present-but-malformed resource (Case 3) — e.g. a
  // `releasability` arriving as a bare string would slip past `.classification` being
  // truthy and flow straight into decideVisibility, where it iterates as characters
  // and can silently merge/leak. Route through the SAME classifier the render path
  // uses instead.
  if (!resource) {
    // Genuinely absent → Case 1, legacy-permissive (load-bearing back-compat).
    return { effect: "permit", obligations: [], reason: "no resource labels (legacy-permissive)" };
  }
  const kase = classifyMarking(resource as CanonicalEnvelope["security"], bundle);
  if (kase === "broken") {
    // Case 3 — a marking we DETECT but cannot resolve (bad shape OR
    // validateMarkings.valid===false). Fail-closed DENY with the CONSTANT Glomar
    // reason (no marking detail leaks to the capability/AI). The descriptive failure
    // goes to the SERVER CONSOLE only — never into a browser-delivered field.
    console.error(
      "[seamPolicy] BROKEN-PRESENT marking at the capability/command seam (fail-closed, DENY): " +
        "marking present but malformed-shape or validateMarkings.valid===false. Push the fix upstream.",
    );
    return { effect: "deny", obligations: [], reason: DENY_REASON };
  }
  if (kase === "unmarked") {
    // Case 1 — genuinely unmarked → legacy-permissive at the default (KEEP behavior).
    return { effect: "permit", obligations: [], reason: "no resource labels (legacy-permissive)" };
  }
  // Case 2 CLEAN — decided normally by the SAME decideVisibility the picture uses.
  const res = decideVisibility(resource, bundle, claims);
  // GLOMAR (drift-critic I8 finding): a DENY reason must reveal NOTHING about the withheld
  // resource. The vendored evaluator's reason describes WHY (e.g. "classification_level: holds
  // UNCLASSIFIED < TOP_SECRET"), which would let a capability/AI infer the level of a thing it
  // cannot see. So a denied decision returns a CONSTANT, non-revealing reason; the descriptive
  // reason stays on the render/audit path (decideVisibility), which only the cleared operator sees.
  return res.presentation.visible
    ? { effect: "permit", obligations: [], reason: res.decision.reason }
    : { effect: "deny", obligations: [], reason: DENY_REASON };
}

/** The data-seam visibility predicate: a capability/AI RECEIVES an envelope only if
 *  cleared to see it. Identical to the render filter (decideVisibility on
 *  env.security), so the seam and the picture show the same set. */
export function isVisibleToSeam(
  env: CanonicalEnvelope,
  bundle: PolicyBundle,
  claims: SubjectClaims,
): boolean {
  // SAME three-case treatment as decideForResource: a BROKEN-PRESENT marking is
  // HIDDEN from the data seam (fail-closed) + loudly logged server-side, never
  // silently fed to a capability/AI. Case 1 (unmarked) / Case 2 (clean) flow through
  // decideVisibility unchanged.
  const kase = classifyMarking(env.security, bundle);
  if (kase === "broken") {
    console.error(
      `[seamPolicy] BROKEN-PRESENT marking at the data seam (fail-closed, HIDDEN): object ${env.id} — ` +
        "marking present but malformed-shape or validateMarkings.valid===false. Push the fix upstream.",
    );
    return false;
  }
  return decideVisibility(env.security, bundle, claims).presentation.visible;
}
